import { useEffect, useRef, useState } from "react";
import { Link } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT, useContent } from "@/data/content";
import { FIRST_PERIOD } from "@/data/firstPeriod";
import { GuideInline } from "@/components/GuideBlock";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookletSection = ({ fp, lang }) => {
  const [product, setProduct] = useState(null);
  useEffect(() => {
    fetch(`${API}/products/booklet`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setProduct(d))
      .catch(() => {});
  }, []);
  const title = (lang === "nl" ? product?.title_nl : product?.title_en) || fp.booklet.title;
  const desc = (lang === "nl" ? product?.desc_nl : product?.desc_en) || fp.booklet.p1;
  const live = !!(product?.available && product?.price_eur != null);
  const price = product?.price_eur != null ? `€${Number(product.price_eur).toFixed(2).replace(".", lang === "nl" ? "," : ".")}` : null;
  return (
    <section className="bg-parchment py-16 lg:py-20" data-testid="fp-booklet">
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          <div className={product?.cover_url ? "grid items-center gap-10 sm:grid-cols-12" : ""}>
            {product?.cover_url && (
              <div className="sm:col-span-4">
                <div className="overflow-hidden rounded-t-[6rem] max-w-[16rem]">
                  <img loading="lazy" src={product.cover_url} alt={title} className="aspect-[3/4] w-full object-cover" data-testid="fp-booklet-cover" />
                </div>
              </div>
            )}
            <div className={product?.cover_url ? "sm:col-span-8" : ""}>
              <p className="text-xs tracking-[0.3em] uppercase text-clay" data-testid="fp-booklet-badge">{live ? fp.booklet.availableBadge : fp.booklet.badge}</p>
              <h2 className="mt-4 font-serif text-2xl sm:text-3xl tracking-tight text-ink" data-testid="fp-booklet-title">{title}</h2>
              <p className="mt-5 text-base leading-relaxed text-soft" data-testid="fp-booklet-desc">{desc}</p>
              {live && (
                <div className="mt-7 flex flex-wrap items-center gap-6">
                  <span className="font-serif text-2xl text-ink" data-testid="fp-booklet-price">{price}</span>
                  <a
                    href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(lang === "nl" ? "Bestelling Eerste Menstruatie-boekje" : "First Period Booklet order")}`}
                    data-testid="fp-booklet-buy"
                    className="group inline-flex items-center gap-3 rounded-full bg-clay px-7 py-3.5 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
                  >
                    {fp.booklet.buyCta}
                    <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Hero = ({ fp, contactLink }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="fp-hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src="/images/menarche.jpg" alt="A quiet desk with a journal, tea and warm light — preparing for a first period" className="h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-ink/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-20 pt-40 lg:px-10">
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight"
            data-testid="fp-hero-title"
          >
            {fp.hero.title}
          </motion.h1>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-7 max-w-2xl text-lg leading-relaxed text-cream"
          data-testid="fp-hero-intro"
        >
          {fp.hero.sub} {fp.hero.intro}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm tracking-wide text-sand"
          data-testid="fp-hero-facts"
        >
          {fp.hero.facts.map((f, i) => (
            <span key={f} className="flex items-center gap-5 whitespace-nowrap">
              {i > 0 && <span className="text-cream/40">·</span>}
              <span>{f}</span>
            </span>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <Link to={contactLink} data-testid="fp-hero-enquire"
            className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust">
            {fp.hero.primaryCta}
            <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a href="#how" data-testid="fp-hero-how"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-300 border-b border-cream/40 pb-1">
            {fp.hero.secondaryCta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const AccordionItem = ({ item, index, prefix }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink/10" data-testid={`${prefix}-${index}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-6 py-5 text-left" data-testid={`${prefix}-toggle-${index}`}>
        <span className="font-serif text-lg sm:text-xl text-ink">{item.q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <p className="pb-6 pr-10 text-base leading-relaxed text-soft">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function FirstPeriodPage() {
  const { lang } = useLang();
  const { offerings } = useContent();
  const fp = FIRST_PERIOD[lang] || FIRST_PERIOD.en;
  useSeo({ ...fp.seo, image: `/images/menarche.jpg` });
  const topicName = offerings.find((o) => o.slug === "first-period")?.name || "First Periods & Growing Up";
  const contactLink = `/contact?topic=${encodeURIComponent(topicName)}`;

  return (
    <main data-testid="first-period-page">
      <Hero fp={fp} contactLink={contactLink} />

      <section id="school" className="bg-parchment border-b border-ink/10 py-16" data-testid="fp-school-band">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-5">{fp.school.heading}</p>
            <ul className="space-y-2 text-base leading-relaxed text-soft" data-testid="fp-school-rows">
              {fp.school.rows.map((r) => <li key={r}>{r}</li>)}
            </ul>
            <div className="mt-8">
              <Link to={contactLink} data-testid="fp-band-enquire"
                className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust">
                {fp.school.cta}
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="fp-why2">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{fp.why2.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{fp.why2.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{fp.why2.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{fp.why2.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{fp.why2.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="fp-explore">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{fp.explore.title}</h2>
          </Reveal>
          <div className="mt-10 space-y-5">
            {fp.explore.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <p className="text-base leading-relaxed text-soft border-l-2 border-clay/30 pl-6" data-testid={`fp-explore-${i}`}>{item}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="bg-cream py-24 lg:py-32" data-testid="fp-how">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{fp.how.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{fp.how.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{fp.how.p2}</p>
            <p className="mt-10 font-serif italic text-2xl leading-snug text-clay border-l-2 border-clay/40 pl-8" data-testid="fp-how-pull">{fp.how.pull}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="fp-questions">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{fp.questions.title}</h2>
          </Reveal>
          <div className="mt-10 space-y-4" data-testid="fp-questions-list">
            {fp.questions.items.map((q, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="font-serif text-xl sm:text-2xl leading-snug text-ink/85 italic border-l-2 border-clay/30 pl-6" data-testid={`fp-question-${i}`}>{q}</p>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 text-base leading-relaxed text-soft">{fp.questions.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="fp-takehome">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{fp.takeHome.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{fp.takeHome.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{fp.takeHome.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{fp.takeHome.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="fp-irida">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src="/irida.jpg" alt="Irida Hysenbelli, founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="fp-irida-photo" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{fp.irida.heading}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft drop-cap">{fp.irida.body}</p>
            <Link to="/about" data-testid="fp-irida-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
              {fp.irida.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 lg:py-24" data-testid="fp-parents">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{fp.parents.title}</h2>
            <p className="mt-5 text-base leading-relaxed text-soft">{fp.parents.p1}</p>
          </Reveal>
          <Reveal>
            <div className="mt-10" data-testid="fp-parents-resources">
              <GuideInline ids={["growing-up-and-periods"]} testid="fp-parents-guide" />
            </div>
          </Reveal>
        </div>
      </section>

      <BookletSection fp={fp} lang={lang} />

      <section className="bg-ink py-24 lg:py-32 grain relative" data-testid="fp-final">
        <div className="mx-auto max-w-3xl px-6 text-center relative z-10">
          <Reveal>
            <p className="font-serif italic text-2xl sm:text-3xl leading-snug text-sand max-w-2xl mx-auto" data-testid="fp-final-pull">{fp.final.pull}</p>
            <h2 className="mt-12 font-serif text-3xl sm:text-4xl tracking-tight text-cream">{fp.final.title}</h2>
            <div className="mt-8 space-y-2" data-testid="fp-final-rows">
              {fp.final.rows.map((r) => (
                <p key={r} className="text-sm text-cream/80">{r}</p>
              ))}
            </div>
            <div className="mt-10">
              <Link to={contactLink} data-testid="fp-final-enquire"
                className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust">
                {fp.final.cta}
                <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
            <Link to="/contact" data-testid="fp-final-contact"
              className="group mt-8 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
              {fp.final.secondary}
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
